import * as React from 'react'
import { theme } from '../theme'
import { ActionButtons } from '../editor/ActionButtons'
import { TextFormattingControls } from './TextFormattingControls'

interface Props {
	value: string
	onChange: (value: string) => void
	maxLength?: number
	disabled?: boolean
	onAccept?: () => void
	isProcessing?: boolean
	areAllAspectRatiosValid?: boolean
	onSwitchToAdjustTool?: () => void
	textAlignment?: 'left' | 'center'
	onTextAlignmentChange?: (alignment: 'left' | 'center') => void
	onIncreaseFontSize?: () => void
	onDecreaseFontSize?: () => void
}

/**
 * Input component for disclaimer text with character counter and accept button
 * Styled to match the editor theme
 */
export const DisclaimerTextInput = ({
	value,
	onChange,
	maxLength = 300,
	disabled = false,
	onAccept,
	isProcessing = false,
	areAllAspectRatiosValid = true,
	onSwitchToAdjustTool,
	textAlignment = 'left',
	onTextAlignmentChange,
	onIncreaseFontSize,
	onDecreaseFontSize,
}: Props): JSX.Element => {
	// Store the previous value for undo functionality
	const [previousValue, setPreviousValue] = React.useState('')
	// Local state for text alignment if no parent handler is provided
	const [localTextAlignment, setLocalTextAlignment] = React.useState<'left' | 'center'>(textAlignment)

	// Use either the prop or local state for alignment
	const currentAlignment = onTextAlignmentChange ? textAlignment : localTextAlignment

	// Update previous value when current value changes
	React.useEffect(() => {
		if (value !== previousValue) {
			setPreviousValue(value)
		}
	}, [value])

	// Handle undo action
	const handleUndo = React.useCallback(() => {
		// Clear the text input
		onChange('')

		// Force a re-render of the canvas by setting a timeout
		// This ensures the canvas is cleared even if there are any race conditions
		requestAnimationFrame(() => {
			onChange('')
		})
	}, [onChange])

	// Determine if the accept button should be disabled
	const isAcceptDisabled = !value || disabled || isProcessing || !areAllAspectRatiosValid

	// Handlers for text formatting controls
	const handleAlignLeft = React.useCallback(() => {
		if (onTextAlignmentChange) {
			onTextAlignmentChange('left')
		} else {
			setLocalTextAlignment('left')
		}
	}, [onTextAlignmentChange])

	const handleAlignCenter = React.useCallback(() => {
		if (onTextAlignmentChange) {
			onTextAlignmentChange('center')
		} else {
			setLocalTextAlignment('center')
		}
	}, [onTextAlignmentChange])

	const handleIncreaseFontSize = React.useCallback(() => {
		if (onIncreaseFontSize) {
			onIncreaseFontSize()
		}
	}, [onIncreaseFontSize])

	const handleDecreaseFontSize = React.useCallback(() => {
		if (onDecreaseFontSize) {
			onDecreaseFontSize()
		}
	}, [onDecreaseFontSize])

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: theme.spacing.md,
				width: '100%',
			}}
		>
			{/* Title */}
			<h3
				style={{
					margin: 0,
					marginBottom: theme.spacing.sm,
					fontSize: theme.typography.sizes.sm,
					color: theme.colors.text.primary,
					fontWeight: 500,
					paddingBottom: theme.spacing.sm,
					borderBottom: `1px solid ${theme.colors.border}`,
				}}
			>
				Add Disclaimer Text
			</h3>

			{/* Instructions */}
			<div style={{ color: theme.colors.text.muted, fontSize: '14px' }}>
				Enter the disclaimer text you want to add to the image. The text will be displayed at the bottom of the
				image with a semi-transparent background. Click the accept button to create a new image with the
				disclaimer text added.
			</div>

			{/* Warning message for invalid aspect ratios */}
			{!areAllAspectRatiosValid && (
				<div
					style={{
						backgroundColor: 'rgba(243, 156, 18, 0.1)',
						border: '1px solid #f39c12',
						borderRadius: theme.borderRadius.sm,
						padding: theme.spacing.sm,
						color: '#c87f0a',
						fontSize: '14px',
						display: 'flex',
						alignItems: 'center',
						gap: theme.spacing.sm,
					}}
				>
					<span style={{ fontSize: '16px' }}>⚠️</span>
					<div>
						<div>
							All images must have the correct aspect ratios before applying disclaimer text. Please
							adjust the images to match their expected dimensions.
						</div>
						{onSwitchToAdjustTool && (
							<button
								onClick={onSwitchToAdjustTool}
								style={{
									background: 'none',
									border: 'none',
									padding: '4px 0',
									margin: '4px 0 0 0',
									color: theme.colors.primary,
									cursor: 'pointer',
									textDecoration: 'underline',
									fontSize: '14px',
									fontWeight: 500,
									display: 'inline-block',
								}}
							>
								Switch to Adjust Tool →
							</button>
						)}
					</div>
				</div>
			)}

			<textarea
				value={value}
				onChange={(e) => onChange(e.target.value)}
				maxLength={maxLength}
				disabled={disabled}
				placeholder="Enter disclaimer text..."
				style={{
					width: '100%',
					minHeight: '80px',
					maxHeight: '120px',
					padding: theme.spacing.sm,
					backgroundColor: theme.colors.background,
					border: `1px solid ${theme.colors.border}`,
					borderRadius: theme.borderRadius.sm,
					color: theme.colors.text.primary,
					fontSize: '14px',
					lineHeight: '1.4',
					resize: 'vertical',
					fontFamily: 'inherit',
					textAlign: currentAlignment, // Apply text alignment
				}}
			/>

			{/* Add text formatting controls */}
			<TextFormattingControls
				currentAlignment={currentAlignment}
				onAlignLeft={handleAlignLeft}
				onAlignCenter={handleAlignCenter}
				onIncreaseFontSize={onIncreaseFontSize ? handleIncreaseFontSize : undefined}
				onDecreaseFontSize={onDecreaseFontSize ? handleDecreaseFontSize : undefined}
				disabled={disabled || isProcessing}
			/>

			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<div style={{ fontSize: theme.typography.sizes.tiny, color: theme.colors.text.muted }}>
					{value.length}/{maxLength} characters
				</div>
				<div style={{ display: 'flex', gap: theme.spacing.sm }}>
					{onAccept && (
						<ActionButtons
							isReturnDisabled={!value}
							isAcceptDisabled={isAcceptDisabled}
							onReturn={handleUndo}
							onAccept={onAccept}
							variant="compact"
							acceptLabel="Add Disclaimers"
						/>
					)}
				</div>
			</div>
		</div>
	)
}
